import { useEffect, useState } from 'react'
import { Box, Container, Heading, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { useAccount } from 'wagmi'

import TokenBalancesTable from '~/components/common/TokenBalancesTable'
import PendingTransactionModal from '~/components/dashboard/PendingTransactionModal'
import { ToastType } from '~/components/dashboard/Toast'
import { CustomButton } from '~/components/layout/CustomButton'
import { CustomConnectKitButton } from '~/components/layout/CustomConnectKitButton'
import NetworkWarningBanner from '~/components/layout/NetworkBanner'
import { FaucetErrorMessages } from '~/constants/faucet'
import { getFaucetEnableStatus } from '~/helpers/network'
import { useMarkets } from '~/hooks/useMarkets'
import useSupportedNetworkId from '~/hooks/useSupportedNetworkId'
import { useToastMessage } from '~/hooks/useToastMessage'
import { useUserIp } from '~/hooks/useUserIp'
import * as Analytics from '~/lib/analytics'
import { requestFaucetTokens } from '~/lib/api'
import { useTranslation } from '~/lib/i18n'
import { checkIfWasSuppliedByFaucetToday } from '~/lib/localStorage'
import { Network } from '~/lib/networks'
import NotFound from './404'

const TOAST_ERROR_DURATION = 6000

const Faucet = () => {
  const { t } = useTranslation()
  const { showToast } = useToastMessage()
  const networkId = useSupportedNetworkId()
  const { isConnected, address, chain: accountChain } = useAccount()

  const { markets } = useMarkets()

  const rpcUrl = accountChain?.rpcUrls.default.http[0] || '' // TODO: Review this logic when implementing the faucet

  const [chainId, setChainId] = useState<Network>()
  const [isSubmitting, setSubmitting] = useState(false)
  const ip = useUserIp()

  const {
    isOpen: isTransactionModalOpen,
    onOpen: onTransactionModalOpen,
    onClose: onTransactionModalClose,
  } = useDisclosure()

  useEffect(() => {
    setChainId(networkId)
  }, [networkId])

  if (!chainId) {
    return null
  }

  const getErrorLabels = (error: Error) => {
    switch (error.message) {
      case FaucetErrorMessages.IP_MISSING:
        return {
          title: t('faucet.ipMissing.title', { ns: 'toast' }),
          description: t('faucet.ipMissing.description', { ns: 'toast' }),
        }

      case FaucetErrorMessages.DAILY_QUOTA_REACHED:
        return {
          title: t('faucet.dailyQuotaReached.title', { ns: 'toast' }),
          description: t('faucet.dailyQuotaReached.description', { ns: 'toast' }),
        }

      case FaucetErrorMessages.WALLET_NOT_CONNECTED:
        return {
          title: t('faucet.walletNotConnected.title', { ns: 'toast' }),
          description: t('faucet.walletNotConnected.description', { ns: 'toast' }),
        }

      case FaucetErrorMessages.UNSUPPORTED_NETWORK:
        return {
          title: t('faucet.networkNotSupported.title', { ns: 'toast' }),
          description: t('faucet.networkNotSupported.description', { ns: 'toast' }),
        }

      case FaucetErrorMessages.CAPTCHA_NOT_VALID:
        return {
          title: t('faucet.captchaNotValid.title', { ns: 'toast' }),
          description: t('faucet.captchaNotValid.description', { ns: 'toast' }),
        }

      case FaucetErrorMessages.DAILY_QUOTA_REACHED_LOCKTIME:
        return {
          title: t('faucet.dailyQuotaReached.title', { ns: 'toast' }),
          description: t('faucet.dailyQuotaReachedLocktime.description', { ns: 'toast' }),
        }

      default:
        return {
          title: t('faucet.generalError.title', { ns: 'toast' }),
          description: error.message,
        }
    }
  }

  const handleError = (e: Error) => {
    showToast({
      variant: ToastType.FAILURE,
      duration: TOAST_ERROR_DURATION,
      ...getErrorLabels(e),
    })

    Analytics.addBreadcrumb(Analytics.BreadcrumbsTypes.FAUCET_ERROR, { message: e.message })
  }

  const onSubmit = async () => {
    setSubmitting(true)

    try {
      if (!ip) {
        throw new Error(FaucetErrorMessages.IP_MISSING)
      }

      const hasBeenSuppliedToday = await checkIfWasSuppliedByFaucetToday(`${address}`, ip)

      if (hasBeenSuppliedToday) {
        throw new Error(FaucetErrorMessages.DAILY_QUOTA_REACHED)
      }

      onTransactionModalOpen()
    } catch (e) {
      handleError(e as Error)
    } finally {
      setSubmitting(false)
    }
  }

  const onModalSubmit = async (captchaToken?: string) => {
    setSubmitting(true)

    try {
      Analytics.addBreadcrumb(Analytics.BreadcrumbsTypes.DRIP_FAUCET_TOKENS_ATTEMPTED)

      if (!chainId) {
        throw new Error(FaucetErrorMessages.WALLET_NOT_CONNECTED)
      }

      await requestFaucetTokens(`${address}`, ip, chainId, markets, rpcUrl, captchaToken)

      showToast({
        variant: ToastType.SUCCESS,
        title: t('faucet.success.title', { ns: 'toast' }),
        description: t('faucet.success.description', { ns: 'toast' }),
      })

      Analytics.addBreadcrumb(Analytics.BreadcrumbsTypes.FAUCET_SUCCESSFUL)
    } catch (e) {
      handleError(e as Error)
    } finally {
      setSubmitting(false)
      onTransactionModalClose()
    }
  }

  const { isFaucetEnabled, networkSupportsFaucet, isFaucetCaptchaEnabled } = getFaucetEnableStatus(chainId)

  if (!isFaucetEnabled) {
    return <NotFound />
  }

  return (
    <>
      {!networkSupportsFaucet && <NetworkWarningBanner />}

      <Container maxW="container.md" ml={{ base: 0, lg: 'auto' }}>
        <Heading mb={2}>{t('faucet.title')}</Heading>
        <Text mb={6} ml={1}>
          {t('faucet.description')}
        </Text>

        <TokenBalancesTable markets={markets} />

        <Box mb={10}>
          {isConnected ? (
            <CustomButton isDisabled={isSubmitting || !isConnected} type="submit" minW={100} onClick={onSubmit}>
              {isSubmitting ? <Spinner size="sm" /> : t('faucet.buttons.request')}
            </CustomButton>
          ) : (
            <CustomConnectKitButton />
          )}
        </Box>

        <PendingTransactionModal
          onSubmit={onModalSubmit}
          isOpen={isTransactionModalOpen}
          onClose={onTransactionModalClose}
          withCaptcha={isFaucetCaptchaEnabled}
          autoSubmit={!isFaucetCaptchaEnabled}
        />
      </Container>
    </>
  )
}

export default Faucet
